import React from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { format } from 'date-fns';
import AuditProgressPipeline from '../../components/common/AuditProgressPipeline';
import {
  AuditStatus,
  AuditType,
  Durchfuehrungszeitraum,
  MitarbeiterList,
  ProjektList,
} from '../../types';
import { AuditFormData } from './auditDetailTypes';
import { PROJEKTKENNUNG_TOOLTIP } from './auditDetailShared';
import { projektnummerFehler } from './projektnummer';
import { localDateTimeInputValueToUtcIso, toLocalDateTimeInputValue } from '../../utils/time';

interface AuditMetaSummaryCardProps {
  isDark: boolean;
  editing: boolean;
  saving: boolean;
  projektnummerAttempted: boolean;
  formData: AuditFormData;
  projekte: ProjektList[];
  auditTypes: AuditType[];
  auditStatuses: AuditStatus[];
  mitarbeiter: MitarbeiterList[];
  zeitraeume: Durchfuehrungszeitraum[];
  typMenuAnchor: HTMLElement | null;
  projektMenuAnchor: HTMLElement | null;
  pentesterMenuAnchor: HTMLElement | null;
  onInputChange: (field: keyof AuditFormData, value: string | number | number[] | undefined) => void;
  onStartEditing: () => void;
  onCancelEditing: () => void;
  onSave: () => void;
  onSetTypMenuAnchor: (value: HTMLElement | null) => void;
  onSetProjektMenuAnchor: (value: HTMLElement | null) => void;
  onSetPentesterMenuAnchor: (value: HTMLElement | null) => void;
  onOpenZeitraumDialog: (zeitraum?: Durchfuehrungszeitraum) => void;
  onDeleteZeitraum: (zeitraumId: number) => void;
}

const labelSx = {
  fontSize: '0.5rem',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  color: 'text.disabled',
  display: 'block',
  mb: 0.25,
};

const valueSx = {
  fontWeight: 500,
  fontSize: '0.7rem',
  lineHeight: 1.2,
};

const AuditMetaSummaryCard: React.FC<AuditMetaSummaryCardProps> = ({
  isDark,
  editing,
  saving,
  projektnummerAttempted,
  formData,
  projekte,
  auditTypes,
  auditStatuses,
  mitarbeiter,
  zeitraeume,
  typMenuAnchor,
  projektMenuAnchor,
  pentesterMenuAnchor,
  onInputChange,
  onStartEditing,
  onCancelEditing,
  onSave,
  onSetTypMenuAnchor,
  onSetProjektMenuAnchor,
  onSetPentesterMenuAnchor,
  onOpenZeitraumDialog,
  onDeleteZeitraum,
}) => {
  const projektnummerMeldung = editing
    ? projektnummerFehler(formData.projektnummer || '', projektnummerAttempted)
    : '';

  const getProjektName = (projektId: number) =>
    projekte.find((projekt) => projekt.id === projektId)?.name;

  const getAuditTypeNames = (ids?: number[]) =>
    (ids || []).map((id) => auditTypes.find((type) => type.id === id)).filter(Boolean) as AuditType[];

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          mb: 2,
          alignItems: 'stretch',
        }}
      >
        <Box
          sx={{
            flex: '0 0 calc(100% / 6 * 1.3)',
            minWidth: 182,
            maxWidth: 286,
            display: 'flex',
            flexDirection: 'column',
            gap: 0.75,
            px: 1.5,
            py: 1.5,
            borderRadius: 2,
            bgcolor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
            position: 'relative',
          }}
        >
          {editing ? (
            <Box sx={{ position: 'absolute', top: 4, right: 4, display: 'flex', gap: 0.25 }}>
              <Tooltip title="Speichern">
                <IconButton
                  size="small"
                  onClick={onSave}
                  disabled={saving || !formData.name || !formData.projekt_id}
                  sx={{
                    width: 22,
                    height: 22,
                    color: '#3dd47e',
                    '&:hover': { bgcolor: 'rgba(61,212,126,0.15)' },
                  }}
                >
                  {saving ? (
                    <CircularProgress size={12} color="inherit" />
                  ) : (
                    <CheckIcon sx={{ fontSize: 14 }} />
                  )}
                </IconButton>
              </Tooltip>
              <Tooltip title="Abbrechen">
                <IconButton
                  size="small"
                  onClick={onCancelEditing}
                  sx={{
                    width: 22,
                    height: 22,
                    color: 'text.disabled',
                    '&:hover': { color: '#ef5350', bgcolor: 'rgba(239,83,80,0.1)' },
                  }}
                >
                  <CloseIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            </Box>
          ) : (
            <Tooltip title="Bearbeiten">
              <IconButton
                size="small"
                onClick={onStartEditing}
                sx={{
                  position: 'absolute',
                  top: 6,
                  right: 6,
                  color: 'text.disabled',
                  '&:hover': { color: '#3dd47e', bgcolor: 'rgba(61,212,126,0.1)' },
                }}
              >
                <EditIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
          )}

          {editing && (
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="caption" sx={labelSx}>
                Name
              </Typography>
              <TextField
                variant="standard"
                size="small"
                value={formData.name}
                onChange={(event) => onInputChange('name', event.target.value)}
                InputProps={{ disableUnderline: true }}
                sx={{ '& input': { fontSize: '0.72rem', fontWeight: 500, p: 0, lineHeight: 1.2 } }}
              />
            </Box>
          )}

          {((formData.audit_type_ids?.length ?? 0) > 0 || editing) && (
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="caption" sx={labelSx}>
                Typ
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.4, flexWrap: 'wrap', alignItems: 'center' }}>
                {getAuditTypeNames(formData.audit_type_ids).map((type) => (
                  <Box
                    key={type.id}
                    component="span"
                    title={type.name}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      px: 0.6,
                      borderRadius: 0.75,
                      fontSize: '0.6rem',
                      fontWeight: 700,
                      color: '#fff',
                      backgroundColor: type.farbe || '#757575',
                      height: 18,
                    }}
                  >
                    {type.kuerzel || type.name}
                    {editing && (
                      <CloseIcon
                        sx={{ fontSize: 11, cursor: 'pointer', ml: 0.2, opacity: 0.8, '&:hover': { opacity: 1 } }}
                        onClick={() =>
                          onInputChange(
                            'audit_type_ids',
                            (formData.audit_type_ids || []).filter((typeId) => typeId !== type.id),
                          )
                        }
                      />
                    )}
                  </Box>
                ))}
                {editing && (
                  <IconButton
                    size="small"
                    onClick={(event) => onSetTypMenuAnchor(event.currentTarget)}
                    sx={{ width: 18, height: 18, bgcolor: 'rgba(61,212,126,0.1)' }}
                  >
                    <AddIcon sx={{ fontSize: 12, color: '#3dd47e' }} />
                  </IconButton>
                )}
              </Box>
            </Box>
          )}

          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" sx={labelSx}>
              Projektnummer
            </Typography>
            {editing ? (
              <TextField
                id="audit-projektnummer-detail"
                variant="standard"
                size="small"
                fullWidth
                value={formData.projektnummer || ''}
                onChange={(event) => onInputChange('projektnummer', event.target.value)}
                placeholder="P-0042"
                error={Boolean(projektnummerMeldung)}
                helperText={projektnummerMeldung || 'P- und nur Ziffern'}
                InputProps={{ disableUnderline: true }}
                inputProps={{ maxLength: 32, autoComplete: 'off', spellCheck: false }}
                sx={{
                  '& input': { fontSize: '0.7rem', fontWeight: 500, p: 0, lineHeight: 1.2 },
                  '& .MuiFormHelperText-root': { mx: 0, mt: 0.25, fontSize: '0.6rem', lineHeight: 1.2 },
                }}
              />
            ) : (
              <Typography sx={valueSx} data-testid="audit-projektnummer-wert">
                {formData.projektnummer || '–'}
              </Typography>
            )}
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" sx={labelSx}>
              Projekt
            </Typography>
            {editing ? (
              <Typography
                onClick={(event) => onSetProjektMenuAnchor(event.currentTarget)}
                sx={{
                  ...valueSx,
                  cursor: 'pointer',
                  '&:hover': { color: 'primary.main' },
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {getProjektName(formData.projekt_id) || 'Auswählen…'}
              </Typography>
            ) : (
              <Typography
                sx={{
                  ...valueSx,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {getProjektName(formData.projekt_id) || '-'}
              </Typography>
            )}
          </Box>

          {(formData.projektkennung || editing) && (
            <Box sx={{ minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mb: 0.25 }}>
                <Typography variant="caption" sx={{ ...labelSx, mb: 0 }}>
                  Projektkennung
                </Typography>
                <Tooltip title={PROJEKTKENNUNG_TOOLTIP} arrow placement="top">
                  <InfoOutlinedIcon sx={{ fontSize: 12, color: 'text.disabled', cursor: 'help' }} />
                </Tooltip>
              </Box>
              {editing ? (
                <TextField
                  variant="standard"
                  size="small"
                  value={formData.projektkennung || ''}
                  onChange={(event) => onInputChange('projektkennung', event.target.value)}
                  placeholder="z.B. ENBW"
                  InputProps={{ disableUnderline: true }}
                  sx={{ '& input': { fontSize: '0.7rem', fontWeight: 500, p: 0, lineHeight: 1.2 } }}
                />
              ) : (
                <Typography sx={valueSx}>{formData.projektkennung || '-'}</Typography>
              )}
            </Box>
          )}

          {(formData.preflight_datum || editing) && (
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="caption" sx={labelSx}>
                Preflight
              </Typography>
              {editing ? (
                <TextField
                  type="datetime-local"
                  variant="standard"
                  size="small"
                  value={toLocalDateTimeInputValue(formData.preflight_datum)}
                  onChange={(event) => onInputChange(
                    'preflight_datum',
                    localDateTimeInputValueToUtcIso(event.target.value),
                  )}
                  InputProps={{ disableUnderline: true }}
                  sx={{ '& input': { fontSize: '0.7rem', fontWeight: 500, p: 0, lineHeight: 1.2 } }}
                />
              ) : (
                <Typography sx={valueSx}>
                  {formData.preflight_datum ? format(new Date(formData.preflight_datum), 'dd.MM.yy') : '-'}
                </Typography>
              )}
            </Box>
          )}

          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" sx={labelSx}>
              Sprache
            </Typography>
            {editing ? (
              <Select
                variant="standard"
                size="small"
                value={formData.sprache || 'de'}
                onChange={(event) => onInputChange('sprache', event.target.value)}
                disableUnderline
                sx={{ fontSize: '0.7rem', fontWeight: 500, '& .MuiSelect-select': { p: 0, lineHeight: 1.2 } }}
              >
                <MenuItem value="de" sx={{ fontSize: '0.75rem' }}>
                  DE
                </MenuItem>
                <MenuItem value="en" sx={{ fontSize: '0.75rem' }}>
                  EN
                </MenuItem>
              </Select>
            ) : (
              <Typography sx={valueSx}>{formData.sprache === 'en' ? 'EN' : 'DE'}</Typography>
            )}
          </Box>

          {((formData.mitarbeiter_ids?.length ?? 0) > 0 || editing) && (
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="caption" sx={labelSx}>
                Pentester
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.35, flexWrap: 'wrap', alignItems: 'center' }}>
                {(formData.mitarbeiter_ids || []).map((mitarbeiterId) => {
                  const person = mitarbeiter.find((entry) => entry.id === mitarbeiterId);
                  const label = person ? `${person.vorname} ${person.nachname}` : String(mitarbeiterId);

                  return (
                    <Chip
                      key={mitarbeiterId}
                      label={label}
                      size="small"
                      variant="outlined"
                      {...(editing && {
                        onDelete: () =>
                          onInputChange(
                            'mitarbeiter_ids',
                            (formData.mitarbeiter_ids || []).filter((entry) => entry !== mitarbeiterId),
                          ),
                        deleteIcon: <CloseIcon sx={{ fontSize: '12px !important' }} />,
                      })}
                      sx={{
                        height: 18,
                        fontSize: '0.62rem',
                        borderColor: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)',
                      }}
                    />
                  );
                })}
                {editing && (
                  <IconButton
                    size="small"
                    onClick={(event) => onSetPentesterMenuAnchor(event.currentTarget)}
                    sx={{ width: 18, height: 18, bgcolor: 'rgba(61,212,126,0.1)' }}
                  >
                    <AddIcon sx={{ fontSize: 12, color: '#3dd47e' }} />
                  </IconButton>
                )}
              </Box>
            </Box>
          )}

          {(zeitraeume.length > 0 || editing) && (
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="caption" sx={labelSx}>
                Zeiträume
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4 }}>
                {zeitraeume.map((zeitraum) => {
                  const datumLabel = `${format(new Date(zeitraum.startdatum), 'dd.MM.')}–${format(
                    new Date(zeitraum.enddatum),
                    'dd.MM.yy',
                  )}`;
                  const parts: string[] = [];
                  if (zeitraum.beschreibung) parts.push(zeitraum.beschreibung);
                  if ((zeitraum.mitarbeiter_team?.length ?? 0) > 0) {
                    parts.push(
                      `Team: ${zeitraum.mitarbeiter_team!.map((m) => `${m.vorname} ${m.nachname}`).join(', ')}`,
                    );
                  }
                  const tooltipText = parts.length > 0 ? parts.join('\n') : null;

                  return (
                    <Tooltip
                      key={zeitraum.id}
                      title={tooltipText || ''}
                      disableHoverListener={!tooltipText}
                      arrow
                      placement="top"
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 0.5,
                          px: 1,
                          py: 0.4,
                          borderRadius: 1,
                          fontSize: '0.6rem',
                          border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)'}`,
                          bgcolor: 'transparent',
                          cursor: editing ? 'pointer' : 'default',
                          '&:hover': editing
                            ? { bgcolor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)' }
                            : {},
                        }}
                        {...(editing && { onClick: () => onOpenZeitraumDialog(zeitraum) })}
                      >
                        <Typography sx={{ fontSize: '0.6rem', fontWeight: 500, flex: 1, minWidth: 0 }}>
                          {datumLabel}
                        </Typography>
                        {tooltipText ? (
                          <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled', flexShrink: 0 }} />
                        ) : editing ? (
                          <Box sx={{ width: 14, flexShrink: 0 }} />
                        ) : null}
                        {editing && (
                          <IconButton
                            size="small"
                            onClick={(event) => {
                              event.stopPropagation();
                              onDeleteZeitraum(zeitraum.id);
                            }}
                            sx={{ width: 18, height: 18, p: 0, flexShrink: 0 }}
                          >
                            <CloseIcon sx={{ fontSize: 12 }} />
                          </IconButton>
                        )}
                      </Box>
                    </Tooltip>
                  );
                })}
                {editing && (
                  <IconButton
                    size="small"
                    onClick={() => onOpenZeitraumDialog()}
                    sx={{
                      width: 18,
                      height: 18,
                      alignSelf: 'flex-start',
                      bgcolor: 'rgba(61,212,126,0.1)',
                    }}
                  >
                    <AddIcon sx={{ fontSize: 12, color: '#3dd47e' }} />
                  </IconButton>
                )}
              </Box>
            </Box>
          )}
        </Box>

        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <AuditProgressPipeline
            statuses={auditStatuses}
            currentStatusId={formData.audit_status_id}
            fillHeight
            {...(editing && {
              onStatusChange: (statusId: number) => onInputChange('audit_status_id', statusId),
            })}
          />
        </Box>
      </Box>

      <Menu
        anchorEl={typMenuAnchor}
        open={Boolean(typMenuAnchor)}
        onClose={() => onSetTypMenuAnchor(null)}
        slotProps={{ paper: { sx: { maxHeight: 280 } } }}
      >
        {auditTypes
          .filter((type) => !(formData.audit_type_ids || []).includes(type.id))
          .map((type) => (
            <MenuItem
              key={type.id}
              onClick={() => {
                onInputChange('audit_type_ids', [...(formData.audit_type_ids || []), type.id]);
                onSetTypMenuAnchor(null);
              }}
              sx={{ fontSize: '0.82rem', gap: 1 }}
            >
              {type.kuerzel && (
                <Box
                  component="span"
                  sx={{
                    px: 0.75,
                    borderRadius: 0.75,
                    fontSize: '0.6rem',
                    fontWeight: 700,
                    color: '#fff',
                    bgcolor: type.farbe || '#757575',
                    lineHeight: '18px',
                  }}
                >
                  {type.kuerzel}
                </Box>
              )}
              {type.name}
            </MenuItem>
          ))}
        {auditTypes.filter((type) => !(formData.audit_type_ids || []).includes(type.id)).length === 0 && (
          <MenuItem disabled sx={{ fontSize: '0.82rem' }}>
            Alle Typen ausgewählt
          </MenuItem>
        )}
      </Menu>

      <Menu
        anchorEl={projektMenuAnchor}
        open={Boolean(projektMenuAnchor)}
        onClose={() => onSetProjektMenuAnchor(null)}
        slotProps={{ paper: { sx: { maxHeight: 280 } } }}
      >
        {projekte.map((projekt) => (
          <MenuItem
            key={projekt.id}
            selected={projekt.id === formData.projekt_id}
            onClick={() => {
              onInputChange('projekt_id', projekt.id);
              onSetProjektMenuAnchor(null);
            }}
            sx={{ fontSize: '0.82rem' }}
          >
            {projekt.name}
          </MenuItem>
        ))}
      </Menu>

      <Menu
        anchorEl={pentesterMenuAnchor}
        open={Boolean(pentesterMenuAnchor)}
        onClose={() => onSetPentesterMenuAnchor(null)}
        slotProps={{ paper: { sx: { maxHeight: 280 } } }}
      >
        {mitarbeiter
          .filter((person) => !(formData.mitarbeiter_ids || []).includes(person.id))
          .map((person) => (
            <MenuItem
              key={person.id}
              onClick={() => {
                onInputChange('mitarbeiter_ids', [...(formData.mitarbeiter_ids || []), person.id]);
                onSetPentesterMenuAnchor(null);
              }}
              sx={{ fontSize: '0.82rem' }}
            >
              {person.vorname} {person.nachname}
            </MenuItem>
          ))}
        {mitarbeiter.filter((person) => !(formData.mitarbeiter_ids || []).includes(person.id)).length === 0 && (
          <MenuItem disabled sx={{ fontSize: '0.82rem' }}>
            Alle Pentester ausgewählt
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

export default AuditMetaSummaryCard;
